import { useEffect } from 'react';

type SeoMetaOptions = {
  title: string;
  description?: string;
  canonicalPath?: string;
  noIndex?: boolean;
  /** Абсолютный или корневой путь к изображению для OG/Twitter */
  imageUrl?: string;
};

const DEFAULT_DESCRIPTION_RU =
  'Sciplex — задачи по программированию, SQL, алгоритмам, математике и физике с автопроверкой в браузере.';
const DEFAULT_DESCRIPTION_EN =
  'Sciplex — programming, SQL, algorithms, math and physics practice problems with instant online checking.';

const DEFAULT_OG_IMAGE_PATH = '/sciplex-logo.png';

function upsertMeta(attr: 'name' | 'property', key: string, content: string) {
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

function upsertLink(rel: string, href: string) {
  let el = document.head.querySelector(`link[rel="${rel}"]`) as HTMLLinkElement | null;
  if (!el) {
    el = document.createElement('link');
    el.rel = rel;
    document.head.appendChild(el);
  }
  el.href = href;
}

function removeMeta(attr: 'name' | 'property', key: string) {
  const el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (el) el.remove();
}

function absoluteAssetUrl(pathOrUrl: string): string {
  if (/^https?:\/\//i.test(pathOrUrl)) return pathOrUrl;
  const origin = typeof window !== 'undefined' ? window.location.origin : 'https://sciplex.ru';
  return `${origin}${pathOrUrl.startsWith('/') ? pathOrUrl : `/${pathOrUrl}`}`;
}

/**
 * Обновляет document.title, description, Open Graph и canonical.
 */
export function useSeoMeta({
  title,
  description,
  canonicalPath,
  noIndex,
  imageUrl,
}: SeoMetaOptions) {
  useEffect(() => {
    const prevTitle = document.title;
    document.title = title;

    const desc = description || DEFAULT_DESCRIPTION_RU;
    const image = absoluteAssetUrl(imageUrl || DEFAULT_OG_IMAGE_PATH);

    upsertMeta('name', 'description', desc);
    upsertMeta('property', 'og:title', title);
    upsertMeta('property', 'og:description', desc);
    upsertMeta('property', 'og:type', 'website');
    upsertMeta('property', 'og:image', image);
    upsertMeta('name', 'twitter:card', 'summary_large_image');
    upsertMeta('name', 'twitter:title', title);
    upsertMeta('name', 'twitter:description', desc);
    upsertMeta('name', 'twitter:image', image);

    if (canonicalPath) {
      const origin = window.location.origin;
      const href = `${origin}${canonicalPath.startsWith('/') ? canonicalPath : `/${canonicalPath}`}`;
      upsertLink('canonical', href);
      upsertMeta('property', 'og:url', href);
    }

    if (noIndex) {
      upsertMeta('name', 'robots', 'noindex, nofollow');
    }

    return () => {
      document.title = prevTitle;
      if (noIndex) {
        removeMeta('name', 'robots');
      }
    };
  }, [title, description, canonicalPath, noIndex, imageUrl]);
}

export function defaultSiteDescription(lang: string): string {
  return lang === 'ru' ? DEFAULT_DESCRIPTION_RU : DEFAULT_DESCRIPTION_EN;
}
